import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { EngagementDistributionChart } from '@/components/charts/EngagementDistributionChart';
import { EngagementTrendline } from '@/components/charts/EngagementTrendline';
import { LowEngagementTimeline } from '@/components/charts/LowEngagementTimeline';
import type { AggregatedEngagement, EngagementSnapshot } from '@/types/emotion';
import { mapToEngagementBuckets } from '@/utils/engagement';

export const PostSessionReports = () => {
  const { sessionId } = useParams();
  const { getToken } = useAuth();

  const { data, isLoading, error } = useQuery({
    enabled: Boolean(sessionId),
    queryKey: ['session-report', sessionId],
    queryFn: async () => {
      const token = await getToken();
      const response = await apiClient.get<{ engagement: AggregatedEngagement[]; meta: Record<string, string> }>(
        `/sessions/${sessionId}/analytics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    }
  });

  const summary = useMemo(() => {
    if (!data) return null;
    const total = data.engagement.length || 1;
    const averages = data.engagement.reduce(
      (acc, curr) => {
        acc.engaged += curr.engaged;
        acc.confused += curr.confused;
        acc.disengaged += curr.disengaged;
        acc.neutral += curr.neutral;
        return acc;
      },
      { engaged: 0, confused: 0, disengaged: 0, neutral: 0 }
    );
    return {
      engaged: Math.round((averages.engaged / total) * 100),
      confused: Math.round((averages.confused / total) * 100),
      disengaged: Math.round((averages.disengaged / total) * 100),
      neutral: Math.round((averages.neutral / total) * 100)
    };
  }, [data]);

  // Calculate session fingerprint (average emotion probabilities)
  const averageSnapshot = useMemo<EngagementSnapshot | null>(() => {
    if (!data?.engagement?.length || !sessionId) return null;

    const total = data.engagement.length;
    
    // Check if we have raw emotion probabilities (prob_* fields)
    const hasRawEmotions = data.engagement.some(
      item => item.prob_happy !== undefined || item.prob_surprise !== undefined
    );

    if (hasRawEmotions) {
      // Calculate averages of raw emotion probabilities
      const emotionTotals = data.engagement.reduce(
        (acc, curr) => {
          acc.happy += curr.prob_happy ?? 0;
          acc.surprise += curr.prob_surprise ?? 0;
          acc.neutral += curr.prob_neutral ?? 0;
          acc.sad += curr.prob_sad ?? 0;
          acc.fear += curr.prob_fear ?? 0;
          acc.disgust += curr.prob_disgust ?? 0;
          acc.angry += curr.prob_angry ?? 0;
          return acc;
        },
        { happy: 0, surprise: 0, neutral: 0, sad: 0, fear: 0, disgust: 0, angry: 0 }
      );

      const averageEmotions = {
        happy: emotionTotals.happy / total,
        surprise: emotionTotals.surprise / total,
        neutral: emotionTotals.neutral / total,
        sad: emotionTotals.sad / total,
        fear: emotionTotals.fear / total,
        disgust: emotionTotals.disgust / total,
        angry: emotionTotals.angry / total,
      };

      // Calculate engagement buckets from raw emotions
      const engagementBuckets = mapToEngagementBuckets(averageEmotions);

      return {
        sessionId,
        timestamp: data.engagement[0]?.timestamp,
        engaged: engagementBuckets.engaged,
        confused: engagementBuckets.confused,
        disengaged: engagementBuckets.disengaged,
        ...averageEmotions,
      };
    } else {
      // Fallback: Use existing engagement averages if raw emotions not available
      // This handles the case where backend hasn't been updated yet
      const engagementTotals = data.engagement.reduce(
        (acc, curr) => {
          acc.engaged += curr.engaged;
          acc.confused += curr.confused;
          acc.disengaged += curr.disengaged;
          acc.neutral += curr.neutral;
          return acc;
        },
        { engaged: 0, confused: 0, disengaged: 0, neutral: 0 }
      );

      return {
        sessionId,
        timestamp: data.engagement[0]?.timestamp,
        engaged: engagementTotals.engaged / total,
        confused: engagementTotals.confused / total,
        disengaged: engagementTotals.disengaged / total,
        // Set raw emotions to 0 if not available (radar chart will show flat)
        happy: 0,
        surprise: 0,
        neutral: engagementTotals.neutral / total,
        sad: 0,
        fear: 0,
        disgust: 0,
        angry: 0,
      };
    }
  }, [data, sessionId]);

  if (isLoading) {
    return <div className="rounded-xl border border-background-border p-6">Loading report...</div>;
  }

  if (error || !data) {
    return <div className="rounded-xl border border-neon-red/40 bg-neon-red/10 p-6 text-neon-red">Unable to load report.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-background-border bg-background-surface p-6">
        <p className="text-sm text-text-dimmed">Session</p>
        <p className="text-2xl font-semibold text-text-primary">{data.meta?.title ?? sessionId}</p>
        <p className="text-xs text-text-dimmed">Ended {data.meta?.endedAt}</p>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <EngagementTrendline data={data.engagement} initialViewMode="lecture" />
        <EngagementDistributionChart summary={summary} latestSnapshot={averageSnapshot} />
      </div>
      <LowEngagementTimeline data={data.engagement} />
    </div>
  );
};

